import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { createPasswordFieldSchema } from 'shared/forms';

import { FORMATTED_RESET_CODE_LENGTH, RESET_CODE_PATTERN, RESET_PASSWORD_FIELDS } from '../consts';

/** Hook that creates the reset-password form Zod schema with i18n validation messages. */
export const useResetPasswordSchema = () => {
  const { t } = useTranslation();

  return z
    .object({
      [RESET_PASSWORD_FIELDS.CODE]: z
        .string()
        .min(1, t('forms.validation.required', { field: t('auth.resetPassword.fields.code') }))
        .max(
          FORMATTED_RESET_CODE_LENGTH,
          t('forms.validation.maxLength', {
            field: t('auth.resetPassword.fields.code'),
            max: FORMATTED_RESET_CODE_LENGTH
          })
        )
        .regex(RESET_CODE_PATTERN, t('auth.resetPassword.errors.invalidCodeFormat')),
      [RESET_PASSWORD_FIELDS.NEW_PASSWORD]: createPasswordFieldSchema(t),
      [RESET_PASSWORD_FIELDS.PASSWORD_CONFIRMATION]: z
        .string()
        .min(1, t('forms.validation.required', { field: t('forms.fields.password') }))
    })
    .superRefine((values, ctx) => {
      if (values[RESET_PASSWORD_FIELDS.NEW_PASSWORD] !== values[RESET_PASSWORD_FIELDS.PASSWORD_CONFIRMATION]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [RESET_PASSWORD_FIELDS.PASSWORD_CONFIRMATION],
          message: t('forms.validation.passwordsDoNotMatch')
        });
      }
    });
};

/** Values managed by the reset-password form. */
export type ResetPasswordFormValues = z.infer<ReturnType<typeof useResetPasswordSchema>>;
